/**
 * What a section IS, as opposed to what it is called.
 *
 * A published clinic column is not fifteen blocks of prose. It opens with a
 * box that answers the reader's four questions in ten seconds, it turns
 「こんな方におすすめ」 into a checklist, it lays the treatment out as numbered
 * steps, and it closes with questions and answers. Those parts are not written
 * like body prose and they do not look like it either - so asking one prompt for
 * all of them produces the thing we had: fifteen indistinguishable sections.
 *
 * The kind is chosen on the plan screen, stored on the section (migration 048,
 * extended by 051), and read by the drafting prompt, which asks for that shape
 * specifically. One vocabulary, shared by the client and the server, because a
 * kind the plan screen offers and the prompt has never heard of is a section
 * that silently comes back as prose.
 */

/** Every shape a section can be written in. */
export const SECTION_KINDS = {
  body: {
    label: '本文',
    /** Shown on the plan screen under the button that adds one. */
    hint: '通常の解説。見出しひとつ分の本文を書きます。',
  },
  summary: {
    label: '冒頭まとめ',
    hint: '記事の頭に置く早見表。効果・回数・痛み・ダウンタイムを表で示します。',
  },
  checklist: {
    label: 'チェックリスト',
    hint: '「こんな方におすすめ」。当てはまる項目を選べる形で並べます。',
  },
  steps: {
    label: '施術の流れ',
    hint: '受付から施術後までを番号つきの手順で示します。',
  },
  faq: {
    label: 'よくある質問',
    hint: '質問と短い答え。検索結果に出やすく、記事の締めに置きます。',
  },
  reasons: {
    label: '選ばれる理由',
    hint: '当院が選ばれる理由。登録済みの強み・体制から3〜5個を短く挙げます。',
  },
} as const;

export type SectionKind = keyof typeof SECTION_KINDS;

export const SECTION_KIND_LIST = Object.keys(SECTION_KINDS) as SectionKind[];

export const isSectionKind = (value: unknown): value is SectionKind =>
  typeof value === 'string' && value in SECTION_KINDS;

/**
 * Kinds a writer may add more than one of.
 *
 * One summary and one FAQ per article: a second 早見表 is a second answer to the
 * same question, and two FAQ blocks split what a search result would have shown
 * as one. A checklist or a flow can honestly appear twice - one per treatment.
 */
export const REPEATABLE: SectionKind[] = ['body', 'checklist', 'steps'];

/**
 * Where a kind belongs when the writer adds it.
 *
 * The summary is the article's first screen or it is pointless; the FAQ closes.
 * 選ばれる理由 sits just before the FAQ in every published example this was
 * modelled on - the last persuasive word before the reader either asks a
 * question or leaves - so it defaults to the end, ahead of a FAQ added after it.
 * Everything else lands where the writer put it.
 */
export const PLACEMENT: Record<SectionKind, 'start' | 'end' | 'here'> = {
  body: 'here',
  summary: 'start',
  checklist: 'here',
  steps: 'here',
  faq: 'end',
  reasons: 'end',
};
